// Updated Theme Version 1.1
import { createTheme } from '@mui/material/styles';

const greenBase = '#4caf50';
const redBase = '#f44336';
const blueBase = '#2196f3';
const yellowBase = '#ffeb3b';

const getPaletteMode = (mode) => {
  return mode.mode === 'light'
    ? {
        primary: {
          main: '#18181b'
        },
        secondary: {
          main: '#f4f4f5'
        },
        background: {
          default: '#fafafa',
          paper: '#ffffff'
        },
        warning: {
          main: '#ff5722'
        },
        error: {
          main: '#e82525'
        },
        success: {
          main: '#24d876'
        },
        info: {
          main: '#2196f3'
        },
        text: {
          primary: '#09090b',
          secondary: '#52525b'
        },
        divider: '#e4e4e7',
        dark: {
          main: '#09090b'
        }
      }
    : {
        primary: {
          main: '#fafafa'
        },
        secondary: {
          main: '#27272a'
        },
        background: {
          main: '#09090b',
          default: '#09090b',
          paper: '#18181b'
        },
        warning: {
          main: '#ff5722'
        },
        error: {
          main: '#e82525'
        },
        success: {
          main: '#24d876'
        },
        info: {
          main: '#2196f3'
        },
        dark: {
          main: '#09090b'
        },
        text: {
          main: '#fafafa',
          primary: '#fafafa',
          secondary: '#a1a1aa'
        },
        divider: '#27272a',
        lightIcon: {
          main: '#fafafa'
        }
      };
};

let createBowlscoutTheme = (mode) => {
  const palette = getPaletteMode(mode);
  return createTheme({
    palette: {
      mode: mode.mode,
      ...palette,
      green: {
        main: greenBase
      },
      red: {
        main: redBase
      },
      blue: {
        main: blueBase
      },
      yellow: {
        main: yellowBase
      }
    },
    shape: {
      borderRadius: 12
    },
    typography: {
      fontFamily: 'Manrope, Segoe UI, Tahoma, sans-serif',
      button: {
        textTransform: 'none',
        fontWeight: 600
      }
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundImage: 'none'
          }
        }
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            border: `1px solid ${palette.divider}`,
            backgroundImage: 'none'
          }
        }
      },
      // AppBar renders as a Paper, so the Paper border above would draw a
      // divider-coloured outline around every top bar. Headers do not use it.
      MuiAppBar: {
        styleOverrides: {
          root: {
            border: 0
          }
        }
      },
      MuiCard: {
        styleOverrides: {
          root: {
            border: `1px solid ${palette.divider}`,
            boxShadow:
              mode.mode === 'dark'
                ? '0 8px 24px rgba(0, 0, 0, 0.35)'
                : '0 2px 10px rgba(24, 24, 27, 0.06)'
          }
        }
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            paddingInline: 16
          },
          containedPrimary: {
            boxShadow: 'none',
            '&:hover': {
              boxShadow: 'none'
            }
          }
        }
      },
      MuiInputBase: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            backgroundColor: mode.mode === 'dark' ? '#18181b' : '#ffffff'
          }
        }
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: palette.divider
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: mode.mode === 'dark' ? '#52525b' : '#a1a1aa'
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: palette.primary.main,
              borderWidth: 2
            }
          }
        }
      },
      MuiDataGrid: {
        styleOverrides: {
          root: {
            border: `1px solid ${palette.divider}`,
            borderRadius: 12
          },
          columnHeaders: {
            borderBottom: `1px solid ${palette.divider}`
          },
          cell: {
            borderBottom: `1px solid ${palette.divider}`
          },
          row: {
            '&:hover': {
              backgroundColor: mode.mode === 'dark' ? '#27272a' : '#fafafa'
            }
          }
        }
      },
      MuiCheckbox: {
        defaultProps: {
          disableRipple: true
        },
        styleOverrides: {
          root: {
            padding: 6,
            color: palette.divider,
            '&:hover': {
              backgroundColor: 'transparent'
            },
            '& .MuiSvgIcon-root': {
              fontSize: 18
            },
            '&.Mui-checked': {
              color: palette.primary.main
            },
            '&.Mui-disabled': {
              color: mode.mode === 'dark' ? '#3f3f46' : '#d4d4d8'
            }
          }
        }
      },
      MuiRadio: {
        defaultProps: {
          disableRipple: true
        },
        styleOverrides: {
          root: {
            padding: 6,
            color: palette.divider,
            '&:hover': {
              backgroundColor: 'transparent'
            },
            '& .MuiSvgIcon-root': {
              fontSize: 18
            },
            '&.Mui-checked': {
              color: palette.primary.main
            },
            '&.Mui-disabled': {
              color: mode.mode === 'dark' ? '#3f3f46' : '#d4d4d8'
            }
          }
        }
      },
      MuiSwitch: {
        styleOverrides: {
          root: {
            width: 36,
            height: 20,
            padding: 0,
            display: 'flex',
            overflow: 'visible',
            marginLeft: 6
          },
          switchBase: {
            padding: 2,
            color: '#ffffff',
            '&.Mui-checked': {
              transform: 'translateX(16px)',
              color: '#ffffff',
              '& + .MuiSwitch-track': {
                backgroundColor: palette.success.main,
                opacity: 1,
                border: 0
              }
            },
            '&.Mui-disabled': {
              color: mode.mode === 'dark' ? '#a1a1aa' : '#ffffff'
            },
            '&.Mui-disabled + .MuiSwitch-track': {
              opacity: 0.5
            },
            '&.Mui-disabled .MuiSwitch-thumb': {
              opacity: 0.7
            }
          },
          thumb: {
            boxSizing: 'border-box',
            width: 16,
            height: 16,
            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.3)'
          },
          track: {
            borderRadius: 10,
            backgroundColor: mode.mode === 'dark' ? '#3f3f46' : '#d4d4d8',
            opacity: 1,
            transition: 'background-color 0.2s ease'
          }
        }
      },
      MuiFormControlLabel: {
        styleOverrides: {
          root: {
            marginLeft: -6,
            gap: 2,
            '&:has(.MuiSwitch-root)': {
              marginBottom: 6
            }
          },
          label: {
            fontSize: '0.9rem'
          }
        }
      }
    }
  });
};

export { createBowlscoutTheme };
